// src/components/chat/ChatMessage.tsx
/**
 * ChatMessage Component
 * Renders a single chat message with avatar and timestamp.
 * User messages render as plain text; assistant messages render as Markdown.
 */

import { memo } from 'react';
import { User, Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import type { IChatMessageProps } from '@/@types/globalChat';

function ChatMessage({ message }: IChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-3 p-3', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'
        )}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>

      {/* Message Content */}
      <div
        className={cn(
          'flex-1 rounded-lg px-3 py-2 max-w-[80%]',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'
        )}
      >
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div
            className={cn(
              'text-sm',
              '[&_p]:mb-2 [&_p:last-child]:mb-0',
              '[&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-2',
              '[&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-2',
              '[&_li]:mb-1',
              '[&_h1]:text-base [&_h1]:font-semibold [&_h1]:mb-2',
              '[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-2',
              '[&_h3]:text-sm [&_h3]:font-medium [&_h3]:mb-1',
              '[&_strong]:font-semibold',
              '[&_em]:italic',
              '[&_code]:bg-background/50 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_code]:font-mono',
              '[&_pre]:bg-background/50 [&_pre]:rounded [&_pre]:p-2 [&_pre]:mb-2 [&_pre]:overflow-x-auto',
              '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
              '[&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:mb-2',
              '[&_table]:w-full [&_table]:border-collapse [&_table]:mb-2',
              '[&_th]:border [&_th]:border-muted-foreground/20 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold [&_th]:bg-background/30',
              '[&_td]:border [&_td]:border-muted-foreground/20 [&_td]:px-2 [&_td]:py-1',
              '[&_a]:underline [&_a]:text-blue-600 dark:[&_a]:text-blue-400',
              '[&_hr]:border-muted-foreground/20 [&_hr]:my-2'
            )}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
          </div>
        )}
        <span className="text-xs opacity-60 mt-1 block">
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>
    </div>
  );
}

const ChatMessageMemo = memo(ChatMessage);

export { ChatMessageMemo };
